/**
 * Bagel Bagel — client wrapper around the askBagel Cloud Function.
 *
 * Handles history persistence in localStorage so the conversation
 * survives page reloads within the same session. History is capped at
 * HISTORY_CAP turns to keep the token budget bounded (the CF also
 * trims to the last 10 turns before calling Anthropic).
 */
import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebase";

export interface BagelMessage {
  role: "user" | "assistant";
  content: string;
  at: number; // epoch ms
}

const HISTORY_KEY = "acework.bagel.history";
const HISTORY_CAP = 30; // keep last 30 turns (both sides count)

export function loadBagelHistory(): BagelMessage[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveBagelHistory(history: BagelMessage[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(-HISTORY_CAP)));
  } catch {
    /* quota / privacy mode */
  }
}

export function clearBagelHistory(): void {
  try {
    localStorage.removeItem(HISTORY_KEY);
  } catch {
    /* ignore */
  }
}

export async function askBagel(
  message: string,
  history: BagelMessage[],
): Promise<{ reply: string; usage: { today: number; cap: number | null } }> {
  const fn = httpsCallable<
    { message: string; history: Array<{ role: string; content: string }> },
    { ok: boolean; reply: string; usage: { today: number; cap: number | null } }
  >(functions, "askBagel");
  const r = await fn({
    message,
    history: history.map((m) => ({ role: m.role, content: m.content })),
  });
  return { reply: r.data.reply, usage: r.data.usage };
}
